import * as React from 'react';
import { Dispatch } from 'react-redux';

import { StoreState } from '../../../../reducers';
import ResourcePanel from '../ResourcePanel';
import { updateEditableEnrichment } from '../../../../containers/Editor/actions';
import { getAspDriveItem } from '../../../../containers/DriveItemList/actions';
import LinkPanel from '../../../../containers/Editor/EditPanel/LinkPanel';
import { Api } from '../../../../typings/api';
import { getDependencyString, getLinkUrlData, LinkUrlData } from '../../../../containers/Editor/utils/helpers';

interface EnrichmentPanelProps {
	page: number;
	id: string;
	resource: Api.AspDriveResourceItemLight;
	defaultResource: Api.AspDriveResourceItemLight;
	importManagerId: string;
	isImporting: boolean;
	dispatch: Dispatch<StoreState>;
}

const getIconName = (type: Api.AssetItemFileTypes): string => {
	switch (type) {
		case Api.AssetItemFileTypes.PUBLICATION:
			return 'iconset_page-bold';
		case Api.AssetItemFileTypes.IMAGE:
			return 'iconset_uprez_image';
		case Api.AssetItemFileTypes.MOVIE:
			return 'iconset_uprez_video';
		case Api.AssetItemFileTypes.COMPONENT:
			return 'iconset_template-bold';
		default:
			return 'iconset_uprez_file';
	}
};

const EnrichmentPanel = ({
	page,
	id,
	resource,
	resource: {
		label,
		type,
		relUrl,
		externalRelUrl,
		isExternalLink,
	},
	defaultResource,
	importManagerId,
	isImporting,
	dispatch,
}: EnrichmentPanelProps) => {
	const handleResourceSelect = async (driveItem: Api.DriveItem): Promise<void> => {
		const aspDriveItem = await dispatch(getAspDriveItem(driveItem.id));

		if (aspDriveItem) {
			const {
				globalId,
				genericItemId,
				parentDriveGId,
				label,
				type,
				relUrl,
			} = aspDriveItem;

			dispatch(updateEditableEnrichment(page, id, {
				resource: {
					...resource,
					globalId,
					genericItemId,
					parentDriveGId,
					label,
					type,
					relUrl,
					deps: getDependencyString(globalId),
					isExternalLink: false,
				},
			}));
		}
	};

	const handleExternalLinkChange = (value: LinkUrlData | null): void => {
		// the x button sends null, the enrichment goes back to the designer's resource
		if (!value) {
			handleResourceReset();

			return;
		}

		dispatch(updateEditableEnrichment(page, id, {
			resource: {
				...resource,
				globalId: null,
				genericItemId: null,
				parentDriveGId: null,
				label: '',
				type: Api.AssetItemFileTypes.DEFAULT,
				relUrl: '',
				externalRelUrl: value.data.href,
				deps: undefined,
				isExternalLink: true,
			},
		}));
	};

	const handleResourceReset = (): void => {
		dispatch(updateEditableEnrichment(page, id, {
			resource: defaultResource,
		}));
	};

	const defaultRelUrl = defaultResource.isExternalLink
		? defaultResource.externalRelUrl
		: defaultResource.relUrl;
	const isDefaultResource = (isExternalLink ? externalRelUrl : relUrl) === defaultRelUrl;

	return (
		<ResourcePanel
			iconName={getIconName(type)}
			label={isDefaultResource ? 'messages.default_file' : label}
			importManagerId={importManagerId}
			isImporting={isImporting}
			isResetButtonDisplayed={!isDefaultResource}
			isExternalLinkActive={isExternalLink}
			linkPanel={(
				<LinkPanel
					linkData={getLinkUrlData(externalRelUrl)}
					onChange={handleExternalLinkChange}
					isUrlLinkOnly={true}
				/>
			)}
			dispatch={dispatch}
			onResourceSelect={handleResourceSelect}
			onResourceReset={handleResourceReset}
		/>
	);
};

export default EnrichmentPanel;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/EditPanel/EnrichmentPanel/index.tsx